import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Sprout } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";

const CATEGORIES = [
  "cattle", "goats", "sheep", "pigs", "crops", "vegetables", "fruit_trees",
  "irrigation", "harvest", "machinery", "water", "diesel", "fertiliser",
];

export default function Farm() {
  const [entries, setEntries] = useState([]);
  async function refresh() {
    try { const { data } = await api.get("/farm/entries"); setEntries(data); }
    catch { toast.error("Could not load Farm360"); }
  }
  useEffect(() => { refresh(); }, []);

  const grouped = CATEGORIES.map((cat) => ({
    category: cat,
    items: entries.filter((e) => e.category === cat),
  }));

  return (
    <div className="space-y-6" data-testid="farm-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Module</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Farm360</h1>
        <p className="subtle mt-1">Livestock, crops, water, machinery — future-ready register.</p>
      </div>

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle className="flex items-center gap-2"><Sprout className="h-4 w-4 text-deepgreen" />All entries</CardTitle><CardDescription>{entries.length} recorded</CardDescription></div>
          <NewEntry onCreated={refresh} />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Category</TableHead><TableHead>Name</TableHead><TableHead>Quantity</TableHead>
              <TableHead>Unit</TableHead><TableHead>Notes</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell><Badge variant="outline">{e.category}</Badge></TableCell>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell>{fmt(e.quantity, 1)}</TableCell>
                  <TableCell>{e.unit}</TableCell>
                  <TableCell className="max-w-md truncate">{e.notes}</TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && <TableRow><TableCell colSpan={5} className="text-center subtle py-8">Farm register is empty. Add cattle, goats, crops etc.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewEntry({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "cattle", name: "", quantity: 0, unit: "", notes: "" });
  async function submit() {
    try { await api.post("/farm/entries", { ...form, quantity: Number(form.quantity) }); toast.success("Entry added"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-farm-btn"><Plus className="h-4 w-4 mr-1" />New entry</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New farm entry</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
          <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="head, kg, ha…" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
